// src/pages/Resources/DeleteGroupModal.tsx
import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaExclamationTriangle } from 'react-icons/fa';

interface Props {
  show: boolean;
  groupName: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteGroupModal: React.FC<Props> = ({ show, groupName, onClose, onConfirm }) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [understood, setUnderstood] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleClose = () => {
    if (!deleting) {
      setStep(1);
      setUnderstood(false);
      setConfirmText('');
      setCopied(false);
      onClose();
    }
  };

  const handleNext = () => {
    if (step === 1) setStep(2);
    else if (step === 2) setStep(3);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await onConfirm();
      handleClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyGroupName = async () => {
    try {
      await navigator.clipboard.writeText(groupName);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const isConfirmTextValid = confirmText.trim().toLowerCase() === groupName.trim().toLowerCase();

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton={!deleting}>
        <Modal.Title className="text-danger">
          <FaExclamationTriangle className="me-2" />
          Delete Group
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Step 1: Warning */}
        {step === 1 && (
          <div>
            <div className="alert alert-danger mb-3">
              <h5 className="alert-heading">⚠️ This action cannot be undone!</h5>
              <p className="mb-0">
                Deleting this group will permanently remove:
              </p>
            </div>
            <ul className="mb-3">
              <li>The Microsoft 365 Group: <strong>{groupName}</strong></li>
              <li>All associated SharePoint sites and files</li>
              <li>All Teams conversations and channels (if Teams-connected)</li>
              <li>All Planner plans and tasks</li>
              <li>All shared resources and calendars</li>
              <li>All member access to group resources</li>
            </ul>
            <div className="alert alert-warning">
              <strong>Important:</strong> This will affect all {' '}
              <span className="text-danger">current members and owners</span> of the group.
            </div>
            <p className="text-muted small">
              If you're unsure, consider removing members instead of deleting the entire group,
              or archive the group for future reference.
            </p>
          </div>
        )}

        {/* Step 2: Acknowledgment */}
        {step === 2 && (
          <div>
            <h5 className="mb-3">Before proceeding, please confirm:</h5>
            <div className="mb-4 p-3 border rounded bg-light">
              <Form.Check 
                type="checkbox"
                id="understand-checkbox"
                label={
                  <span>
                    I understand that <strong>deleting "{groupName}"</strong> is permanent
                    and <strong className="text-danger">cannot be undone</strong>
                  </span>
                }
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
                className="mb-2"
              />
            </div>
            <div className="alert alert-info small">
              <strong>Note:</strong> After deletion, the group name may be unavailable for
              immediate reuse. Users will lose all access to group resources.
            </div>
          </div>
        )}

        {/* Step 3: Final Confirmation */}
        {step === 3 && (
          <div>
            <h5 className="mb-3 text-danger">Final Confirmation Required</h5>
            <p>
              To confirm deletion, please type the group name exactly as shown below:
            </p>
            <div className="mb-3 p-3 border rounded bg-light d-flex align-items-center justify-content-between gap-3">
              <code className="text-primary fs-6">{groupName}</code>
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                {copied && <span className="text-success small">Copied</span>}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleCopyGroupName}
                  disabled={deleting}
                >
                  Copy
                </Button>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Enter group name:</Form.Label>
              <Form.Control
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type group name here"
                autoFocus
                disabled={deleting}
              />
            </Form.Group>
            {confirmText && !isConfirmTextValid && (
              <div className="text-danger small mb-2">
                ❌ Group name doesn't match. Please type it exactly.
              </div>
            )}
            {isConfirmTextValid && (
              <div className="text-success small mb-2">
                ✓ Group name matches
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={handleClose}
          disabled={deleting}
        >
          Cancel
        </Button>
        
        {step < 3 && (
          <Button 
            variant="warning" 
            onClick={handleNext}
            disabled={step === 2 && !understood}
          >
            {step === 1 ? 'Continue' : 'Proceed to Final Step'}
          </Button>
        )}

        {step === 3 && (
          <Button 
            variant="danger" 
            onClick={handleConfirmDelete}
            disabled={!isConfirmTextValid || deleting}
          >
            {deleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Deleting...
              </>
            ) : (
              'DELETE GROUP PERMANENTLY'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteGroupModal;
